import React, { useState, useEffect } from "react";
import { Product } from "../../../types";
import "./reorder-modal.css";

interface ReorderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newOrder: number) => void;
  product: Product | null;
  isLoading?: boolean;
}

export const ReorderModal: React.FC<ReorderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  product,
  isLoading = false,
}) => {
  const [newOrder, setNewOrder] = useState<string>("");

  // Reset form when modal opens/closes or product changes
  useEffect(() => {
    if (isOpen && product) {
      setNewOrder(String(product.order || 0));
    } else {
      setNewOrder("");
    }
  }, [isOpen, product]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const orderNumber = Number(newOrder);
    if (!isNaN(orderNumber) && orderNumber >= 0) {
      onConfirm(orderNumber);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const isValidInput = () => {
    const orderNumber = Number(newOrder);
    return !isNaN(orderNumber) && orderNumber >= 0;
  };

  if (!isOpen || !product) return null;

  return (
    <div className="reorder-modal-overlay" onClick={handleOverlayClick}>
      <div className="reorder-modal-content" onClick={handleContentClick}>
        <div className="reorder-modal-header">
          <h2 className="reorder-modal-title">Изменить порядок продукта</h2>
          <button
            className="reorder-modal-close"
            onClick={handleClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="reorder-modal-body">
          <div className="reorder-modal__product-info">
            <h3 className="reorder-modal__product-name">
              {product.brand} {product.model}
            </h3>
            <p className="reorder-modal__product-details">
              Лот: {product.lotNumber} • Год: {product.year}
            </p>
            <p className="reorder-modal__current-order">
              Текущий порядок: <strong>{product.order || 0}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="reorder-modal__form">
            <div className="reorder-modal__field">
              <label htmlFor="newOrder" className="reorder-modal__label">
                Новый порядок:
              </label>
              <input
                id="newOrder"
                type="number"
                min="0"
                value={newOrder}
                onChange={(e) => setNewOrder(e.target.value)}
                className="reorder-modal__input"
                placeholder="Введите новый порядок"
                disabled={isLoading}
                autoFocus
              />
              <p className="reorder-modal__help">
                Введите число 0 или больше. Продукты с порядком 0 будут показаны
                последними.
              </p>
            </div>
          </form>
        </div>

        <div className="reorder-modal-footer">
          <button
            type="button"
            onClick={handleClose}
            className="reorder-modal__btn reorder-modal__btn--cancel"
            disabled={isLoading}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => {
              const orderNumber = Number(newOrder);
              if (isValidInput()) {
                onConfirm(orderNumber);
              }
            }}
            className="reorder-modal__btn reorder-modal__btn--confirm"
            disabled={!isValidInput() || isLoading}
          >
            {isLoading ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReorderModal;
